import React, { useEffect, useState } from 'react';
import { Box, Alert, CircularProgress } from '@mui/material';
import { Typography, Button, Card } from '../design-system';
import { DESIGN_TOKENS } from '../design-system/tokens/colors';
import { SPACING_TOKENS } from '../design-system/tokens/spacing';
import { useAuth } from '../hooks/useAuth';
import { asaasService } from '../services/asaasService';
import { useNavigate } from 'react-router-dom';

const CancelSubscriptionPage: React.FC = () => {
  const { authState } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [currentPeriodEnd, setCurrentPeriodEnd] = useState<number | null>(null);
  const [cancelAtPeriodEnd, setCancelAtPeriodEnd] = useState<boolean>(false);
  const [subscriptionId, setSubscriptionId] = useState<string | null>(null);

  const getNormalizedPayments = (raw: any): any[] => {
    if (Array.isArray(raw?.data?.data)) return raw.data.data;
    if (Array.isArray(raw?.data?.items)) return raw.data.items;
    if (Array.isArray(raw?.data)) return raw.data;
    if (Array.isArray(raw)) return raw;
    return [];
  };

  const getEarliestPendingPayment = (payments: any[]) => {
    return [...payments]
      .filter((payment: any) => ['PENDING', 'AWAITING_RISK_ANALYSIS'].includes(String(payment?.status || '').toUpperCase()))
      .filter((payment: any) => Number.isFinite(Date.parse(String(payment?.dueDate || ''))))
      .sort(
        (a: any, b: any) =>
          Date.parse(String(a?.dueDate || '')) - Date.parse(String(b?.dueDate || '')),
      )[0] || null;
  };

  useEffect(() => {
    const fetchStatus = async () => {
      try {
        setLoading(true);
        setError(null);
        const email = authState.user?.email;
        if (!email) {
          setError('Usuário não autenticado.');
          setLoading(false);
          return;
        }

        const access: any = await asaasService.checkAccessByEmail(email);
        const subId: string | null = access?.subscription?.id || null;
        if (!subId) {
          setError('Não foi possível carregar sua assinatura.');
        } else {
          setSubscriptionId(subId);
          const [subResp, paymentsResp]: any = await Promise.all([
            asaasService.getSubscription(subId),
            asaasService.listSubscriptionPayments(subId),
          ]);
          const data: any = subResp?.data || subResp || {};
          const payments = getNormalizedPayments(paymentsResp);
          const nextDueDate: string | undefined = data?.nextDueDate;
          const endDate: string | undefined = data?.endDate;
          setCancelAtPeriodEnd(Boolean(endDate));
          const earliestPendingPayment = getEarliestPendingPayment(payments);
          const displayEndDate =
            !endDate &&
            earliestPendingPayment?.dueDate &&
            nextDueDate &&
            Date.parse(String(earliestPendingPayment.dueDate)) < Date.parse(String(nextDueDate))
              ? new Date(Date.parse(String(earliestPendingPayment.dueDate)) - 24 * 60 * 60 * 1000).toISOString().slice(0, 10)
              : endDate || nextDueDate;
          if (displayEndDate) {
            const ts = Math.floor(Date.parse(displayEndDate) / 1000);
            setCurrentPeriodEnd(Number.isFinite(ts) ? ts : null);
          } else {
            setCurrentPeriodEnd(null);
          }
        }
      } catch (e: any) {
        setError(e.message || 'Erro ao obter status da assinatura.');
      } finally {
        setLoading(false);
      }
    };
    fetchStatus();
  }, [authState.user?.email]);

  const handleCancel = async () => {
    try {
      setError(null);
      setSuccess(null);
      const email = authState.user?.email;
      if (!email) {
        setError('Usuário não autenticado.');
        return;
      }
      if (!subscriptionId) {
        setError('Assinatura não localizada.');
        return;
      }
      const res: any = await asaasService.scheduleCancelAtPeriodEnd(subscriptionId);
      const endDate: string | undefined = res?.data?.endDate || res?.endDate;
      const noFutureCharge = res?.nextChargeDate === null || res?.data?.nextChargeDate === null;
      setSuccess(noFutureCharge ? 'Cancelamento confirmado. Nenhuma cobrança futura será gerada.' : 'Cancelamento agendado com sucesso.');
      setCancelAtPeriodEnd(true);
      if (endDate) {
        const ts = Math.floor(Date.parse(endDate) / 1000);
        setCurrentPeriodEnd(Number.isFinite(ts) ? ts : currentPeriodEnd);
      }
    } catch (e: any) {
      setError(e.message || 'Erro ao cancelar assinatura.');
    }
  };

  const formatDate = (ts?: number | null) => {
    if (!ts) return '—';
    const dt = new Date(ts * 1000);
    return dt.toLocaleDateString('pt-BR', { year: 'numeric', month: 'long', day: 'numeric' });
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: DESIGN_TOKENS.colors.background.default, py: SPACING_TOKENS.semantic.xl }}>
      <Box sx={{ maxWidth: 720, mx: 'auto' }}>
        <Card variant="outlined" padding="large">
          <Typography variant="h5" sx={{ fontWeight: 700, mb: SPACING_TOKENS.semantic.md }}>
            Cancelamento de Assinatura
          </Typography>
          <Typography variant="body1" sx={{ mb: SPACING_TOKENS.semantic.md }}>
            Ao confirmar o cancelamento, você continuará com acesso à plataforma até:
          </Typography>
          {loading ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <CircularProgress sx={{ color: DESIGN_TOKENS.colors.primary[500] }} />
              <Typography variant="body2">Carregando detalhes da assinatura...</Typography>
            </Box>
          ) : (
            <Box sx={{ p: SPACING_TOKENS.semantic.md, borderRadius: 2, bgcolor: DESIGN_TOKENS.colors.primary[50], mb: SPACING_TOKENS.semantic.md }}>
              <Typography variant="h6" sx={{ fontWeight: 700, color: DESIGN_TOKENS.colors.primary[600] }}>
                {formatDate(currentPeriodEnd)}
              </Typography>
              <Typography variant="body2" sx={{ color: DESIGN_TOKENS.colors.text.secondary }}>
                Após essa data, você não terá mais acesso aos recursos da aplicação.
              </Typography>
              {cancelAtPeriodEnd && (
                <Alert severity="info" sx={{ mt: SPACING_TOKENS.semantic.sm }}>
                  Seu cancelamento já está programado para o fim do período.
                </Alert>
              )}
            </Box>
          )}
          {error && <Alert severity="error" sx={{ mb: SPACING_TOKENS.semantic.md }}>{error}</Alert>}
          {success && <Alert severity="success" sx={{ mb: SPACING_TOKENS.semantic.md }}>{success}</Alert>}

          <Box sx={{ display: 'flex', gap: SPACING_TOKENS.semantic.sm }}>
            <Button variant="secondary" onClick={() => navigate('/dashboard', { state: { contentView: 'profile' } })}>Voltar ao Perfil</Button>
            {!cancelAtPeriodEnd && (
              <Button variant="primary" onClick={handleCancel} disabled={loading}>
                Confirmar cancelamento
              </Button>
            )}
          </Box>
        </Card>
      </Box>
    </Box>
  );
};

export default CancelSubscriptionPage;
